import React from "react";

import { useService } from "@xstate/react";

import { productBuilderMachine } from "../../../context/ProductBuilder/machine";
import { getBaseProduct } from "../../../helpers/getBaseProduct";
import { getCurtainRailHexColour } from "../../../helpers/getHexColorCode";
import { getFormatedPrice } from "../../../helpers/getFormatedPrice";
import { isCurrentState } from "../../../helpers/isCurrentState";
import { isCurtain } from "../../../helpers/isCurtain";
import { throwError } from "../../../helpers/throwError";
import { ProductBuilderMachineEvent } from "../../../types";
import { Caption } from "../../Common/Caption";
import { Extra } from "../../Common/Extra";
import { Heading } from "../../Common/Heading";
import { Selector, SelectorColour, SelectorItem } from "../../Common/Selector";
import { SectionContent } from "./SectionContent";

type OnClick = (
  trackColour: string,
  variantId: number,
  price: number,
  send: (event: ProductBuilderMachineEvent) => void
) => () => void;

const onClick: OnClick = (trackColour, variantId, price, send) => () => {
  send({
    type: "UPDATE_CUSTOM",
    custom: {
      trackColour,
      track_colour_variant_id: variantId,
      track_colour_price: price,
    } as any,
  });
};

export const TrackColour = () => {
  const [state, send] = useService(productBuilderMachine);
  const baseProduct = getBaseProduct(state.context);
  const {
    custom: { trackColour, trackRequire, measurements, track_colour_variant_id, track_colour_price },
    trackblack,
    trackwhite,
  } = state.context;

  // Check if this is a curtain type
  const isCurtainType = isCurtain(state.context);

  // Calculate track colour options directly from context data (like WandColour)
  const getTrackColourOptions = () => {
    const options: Array<{
      id: string;
      title: string;
      price: number;
      variantId: number;
    }> = [];

    // Add white track option first (preferred default)
    if (trackwhite && Array.isArray(trackwhite) && trackwhite.length > 0) {
      const whiteProduct = trackwhite[0];
      if (
        whiteProduct &&
        whiteProduct.variants &&
        Array.isArray(whiteProduct.variants) &&
        whiteProduct.variants.length > 0
      ) {
        // Find variant that covers the required width based on measurements
        const [width] = measurements
          ? measurements.split("x").map((v: string) => parseInt(v))
          : [0];
        const selectedVariant = width
          ? whiteProduct.variants.find((variant) => {
              const variantWidth = parseInt(variant.title.replace("mm", ""));
              return variantWidth >= width;
            }) || whiteProduct.variants[0]
          : whiteProduct.variants[0]; // Fallback to first if no match
        if (selectedVariant) {
          options.push({
            id: "white",
            title: selectedVariant.name || `White - ${selectedVariant.title}`,
            price: selectedVariant.price,
            variantId: selectedVariant.id,
          });
        }
      }
    }

    // Add black track option second
    if (trackblack && Array.isArray(trackblack) && trackblack.length > 0) {
      const blackProduct = trackblack[0];
      if (
        blackProduct &&
        blackProduct.variants &&
        Array.isArray(blackProduct.variants) &&
        blackProduct.variants.length > 0
      ) {
        // Find variant that covers the required width based on measurements
        const [width] = measurements
          ? measurements.split("x").map((v: string) => parseInt(v))
          : [0];
        const selectedVariant = width
          ? blackProduct.variants.find((variant) => {
              const variantWidth = parseInt(variant.title.replace("mm", ""));
              return variantWidth >= width;
            }) || blackProduct.variants[0]
          : blackProduct.variants[0]; // Fallback to first if no match
        if (selectedVariant) {
          options.push({
            id: "black",
            title: selectedVariant.name || `Black - ${selectedVariant.title}`,
            price: selectedVariant.price,
            variantId: selectedVariant.id,
          });
        }
      }
    }

    return options;
  };

  const trackColourOptions = getTrackColourOptions();

  // Fallback to metafield options for backward compatibility
  const metafieldOptions = baseProduct
    ? baseProduct?.metafields.track_colour || []
    : [];

  // Use dynamic options if available, otherwise fallback to metafield options
  const displayOptions =
    trackColourOptions.length > 0
      ? trackColourOptions.map((option) => option.id)
      : metafieldOptions.length > 0
      ? metafieldOptions
      : ["white", "black"]; // Fallback to basic options (white first)

  const selectedOption = trackColourOptions.find(
    (option) => option.id === trackColour
  );

  // Helper function to find variant price by variant ID or recalculate based on current measurements
  const findVariantPrice = (variantId: number, currentTrackColour?: string): number => {
    if (!variantId || !measurements) return 0;
    
    const [width] = measurements.split("x").map((v: string) => parseInt(v));
    if (!width) return 0;
    
    // Determine which product to search based on track colour or try both
    const searchProducts = currentTrackColour === "black" 
      ? [trackblack, trackwhite]
      : currentTrackColour === "white"
      ? [trackwhite, trackblack]
      : [trackwhite, trackblack]; // Default to white first
    
    for (const trackProduct of searchProducts) {
      if (trackProduct && Array.isArray(trackProduct) && trackProduct.length > 0) {
        const product = trackProduct[0];
        if (product?.variants) {
          // First try to find exact variant ID match
          const exactVariant = product.variants.find((v: any) => v.id === variantId);
          if (exactVariant) {
            // Verify the variant width covers the current measurements
            const variantWidth = parseInt(exactVariant.title.replace("mm", ""));
            if (variantWidth >= width) {
              return exactVariant.price;
            }
          }
          
          // If exact match doesn't work, find variant that covers current width
          const matchingVariant = product.variants.find((variant: any) => {
            const variantWidth = parseInt(variant.title.replace("mm", ""));
            return variantWidth >= width;
          });
          if (matchingVariant) {
            return matchingVariant.price;
          }
        }
      }
    }
    
    return 0;
  };

  // Consolidated effect to handle all track color initialization and state management
  React.useLayoutEffect(() => {
    const onTrackColourStep = isCurrentState("trackColour", state);

    // Only recalculate price when user is on this step (not automatically)
    // This ensures addon prices are only added when user reaches this step
    if (
      onTrackColourStep &&
      trackRequire === "Yes" &&
      track_colour_variant_id &&
      (!track_colour_price || track_colour_price === 0) &&
      measurements // Only recalculate if measurements are available
    ) {
      const recalculatedPrice = findVariantPrice(track_colour_variant_id, trackColour);
      if (recalculatedPrice > 0) {
        send({
          type: "UPDATE_CUSTOM",
          custom: {
            track_colour_price: recalculatedPrice,
          } as any,
        });
        return; // Exit early after recalculating price
      }
    }

    // For curtain types: Auto-select "No" for track requirement if not set
    if (
      isCurtainType &&
      onTrackColourStep &&
      (!trackRequire || trackRequire === "") &&
      !trackColour
    ) {
      send({
        type: "UPDATE_CUSTOM",
        custom: {
          trackRequire: "No",
          trackColour: "No",
        },
      });
      return; // Exit early to prevent other logic from running
    }

    // Auto-select default track color when switching from No to Yes
    // Only do this if measurements are available to calculate correct price
    if (
      isCurtainType &&
      trackRequire === "Yes" &&
      (!trackColour || trackColour === "No") &&
      trackColourOptions.length > 0 &&
      measurements // Ensure measurements are available before calculating price
    ) {
      // Prefer white as default, fallback to first available option
      const whiteOption = trackColourOptions.find((opt) => opt.id === "white");
      const defaultOption = whiteOption || trackColourOptions[0];
      send({
        type: "UPDATE_CUSTOM",
        custom: {
          trackColour: defaultOption.id,
          track_colour_variant_id: defaultOption.variantId,
          track_colour_price: defaultOption.price,
        } as any,
      });
      return; // Exit early to prevent other logic from running
    }

    // Preselect 'White' when entering this step if available and nothing selected yet (for non-curtain types)
    if (
      onTrackColourStep &&
      !isCurtainType &&
      (!trackColour || trackColour === "") &&
      Array.isArray(displayOptions) &&
      displayOptions.length > 0 &&
      displayOptions.includes("white")
    ) {
      const whiteOption = trackColourOptions.find(
        (option) => option.id === "white"
      );
      if (whiteOption) {
        send({
          type: "UPDATE_CUSTOM",
          custom: {
            trackColour: "white",
            track_colour_variant_id: whiteOption.variantId,
            track_colour_price: whiteOption.price,
          } as any,
        });
      } else {
        send({
          type: "UPDATE_CUSTOM",
          custom: {
            trackColour: "white",
          },
        });
      }
    }
  }, [
    state.value,
    trackRequire,
    trackColour,
    trackColourOptions,
    displayOptions,
    isCurtainType,
    track_colour_variant_id,
    track_colour_price,
    measurements,
    send,
  ]);

  // Only check displayOptions if we need to show color options
  if (
    (!isCurtainType || trackRequire === "Yes") &&
    (!Array.isArray(displayOptions) || displayOptions.length === 0)
  ) {
    if (isCurrentState("trackColour", state)) {
      throwError(state);
    }
  }

  return (
    <SectionContent>
      <Heading>{isCurtainType ? "Curtain Track Rail" : "Track Colour"}</Heading>
     {isCurtainType && <div
        style={{
          display: "flex",
          justifyContent: "center",
          paddingTop: "20px",
        }}
      >
        <span>Whould you like a curtain track rail?</span>
      </div>}

      {/* For curtain types: Show Yes/No selection first */}
      {isCurtainType && (
        <>
          <Selector isSlider={true}>
            <div style={{ width: "200px", display: "inline-block" }}>
              <SelectorItem
                isSelected={trackRequire === "Yes"}
                onClick={() => {
                  send({
                    type: "UPDATE_CUSTOM",
                    custom: {
                      trackRequire: "Yes",
                      trackColour: undefined, // Clear trackColour to trigger auto-selection
                    } as any,
                  });
                }}
              >
                Yes, I require a track
              </SelectorItem>
            </div>
            <div style={{ width: "200px", display: "inline-block" }}>
              <SelectorItem
                isSelected={trackRequire === "No"}
                onClick={() => {
                  send({
                    type: "UPDATE_CUSTOM",
                    custom: {
                      trackRequire: "No",
                      trackColour: "No",
                      track_colour_variant_id: undefined,
                      track_colour_price: 0,
                    } as any,
                  });
                }}
              >
                No, not required
              </SelectorItem>
            </div>
          </Selector>
        </>
      )}

      {/* Show color options only if Yes is selected (for curtains) or not a curtain type */}
      {(!isCurtainType || trackRequire === "Yes") && (
        <Selector isSingleOption={true}>
          {displayOptions.map((option) => {
            const optionData = trackColourOptions.find(
              (opt) => opt.id === option
            );
            return (
              <SelectorColour
                isSelected={option === trackColour}
                disableTooltip={true}
                onClick={
                  optionData
                    ? onClick(
                        option,
                        optionData.variantId,
                        optionData.price,
                        send
                      )
                    : onClick(option, 0, 0, send)
                }
                key={option}
                colourName={optionData ? optionData.title : option}
                colourCode={getCurtainRailHexColour(option)}
                colourSwatch={""}
              />
            );
          })}
        </Selector>
      )}

      <Caption>
        {isCurtainType && trackRequire === "No"
          ? ""
          : selectedOption
          ? selectedOption.title
          : trackColour}
      </Caption>
      {/* {selectedOption && (
        <Extra>{getFormatedPrice(selectedOption.price)}</Extra>
      )} */}
    </SectionContent>
  );
};
